import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  cn(
    // Radius = pill (radius-button 999 w systemie Workflow 3.0). Nadpisywalne per instancja przez `className`.
    'inline-flex items-center justify-center rounded-full font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent-600',
    // Disabled — WSPÓLNY dla wszystkich wariantów (Figma: surface/disabled + text/disabled,
    // NIGDY opacity — półprzezroczysty przycisk trzymałby kolor marki/czerwieni pod spodem).
    'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-bg-secondary disabled:text-text-muted disabled:shadow-none',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800',
        // Figma „Secondary" — biały + cienka ramka (NIE wypełnienie szarym, to było pomylone
        // z „Tertiary"/neutralnym stylem). `border-secondary` = `color/border/default` z Figmy.
        secondary:
          'bg-bg-primary text-text-primary border border-border-secondary hover:bg-bg-tertiary active:bg-bg-secondary',
        // Figma „Tertiary" — subtelne niebieskie tło + niebieski tekst (link-style). Nowy
        // wariant, wcześniej nieobecny w kodzie.
        tertiary: 'bg-accent-50 text-accent-600 hover:bg-accent-100 hover:text-accent-700',
        ghost: 'bg-transparent text-text-primary hover:bg-bg-tertiary active:bg-bg-secondary',
        danger:
          'bg-[rgb(var(--color-error))] text-white hover:bg-[rgb(var(--color-error-hover))] active:bg-[rgb(var(--color-error-active))]',
      },
      size: {
        sm: 'h-8 px-3 text-[13px] leading-[20px] gap-1.5',
        md: 'h-10 px-4 text-[14px] leading-[20px] gap-2',
        lg: 'h-11 px-5 text-[14px] leading-[20px] gap-2',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    leftIcon,
    rightIcon,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      {...rest}
    >
      {loading ? <Spinner size={size} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

function Spinner({ size }: { size: ButtonSize }) {
  const px = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      className="animate-spin"
      aria-hidden="true"
      role="img"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeOpacity="0.25"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
